import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync, faFileCsv, faPercent } from '@fortawesome/free-solid-svg-icons';
import AccountsUsersService, { downloadBlob } from '../../../services/AccountsUsersService';
import { Loading } from '../../Loading';
import Paginator from '../../Paginator';
import ConfirmationModal from '../../ConfirmationModal';
import { AccountsTable } from './AccountsTable';
import { AccountsFilters } from './AccountsFilters';
import { AccountModal } from './AccountModal';
import { AccountServicesModal } from './AccountServicesModal';
import { BillingDataModal } from './BillingDataModal';
import { BankInfoModal } from './BankInfoModal';
import { PercentPublishersModal } from '../percent/PercentPublishersModal';

const EMPTY_FILTERS = { type: '', tier: '', service: '', prefix: '', search: '' };

export function AccountsTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState('');

    // Applied filters + drafts for text inputs (search/prefix) to avoid per-keystroke requests.
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [drafts, setDrafts] = useState({ prefix: '', search: '' });

    // Modals
    const [accountModal, setAccountModal] = useState({ open: false, isNew: true, account: null });
    const [servicesModal, setServicesModal] = useState({ open: false, account: null });
    const [billingModal, setBillingModal] = useState({ open: false, account: null });
    const [bankModal, setBankModal] = useState({ open: false, account: null });
    const [percentModal, setPercentModal] = useState({ open: false, account: null, initialTab: 'add' });
    const [deactivateTarget, setDeactivateTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await AccountsUsersService.getAccounts({
                page, per_page: perPage, ...filters,
            });
            setItems(res.items || []);
            setTotal(res.total || 0);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [page, perPage, filters]);

    useEffect(() => {
        fetchAccounts();
        return () => { setItems([]); };
    }, [fetchAccounts, refreshKey]);

    const applyFilter = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
        setPage(1);
        setRefreshKey((k) => k + 1);
    };

    const handleFilterChange = (name, value) => {
        if (name === 'search' || name === 'prefix') {
            setDrafts((prev) => ({ ...prev, [name]: value }));
        } else {
            applyFilter(name, value);
        }
    };

    const handleSearch = () => {
        setFilters((prev) => ({ ...prev, search: drafts.search, prefix: drafts.prefix.toUpperCase() }));
        setPage(1);
        setRefreshKey((k) => k + 1);
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setDrafts({ prefix: '', search: '' });
        setPage(1);
        setRefreshKey((k) => k + 1);
    };

    const flash = (msg) => {
        setFeedback(msg);
        setTimeout(() => setFeedback(''), 4000);
    };

    // Create / edit
    const openCreate = () => setAccountModal({ open: true, isNew: true, account: null });
    const openEdit = (acc) => setAccountModal({ open: true, isNew: false, account: acc });

    const handleSaveAccount = async (payload) => {
        setSaving(true);
        try {
            if (accountModal.isNew) {
                const res = await AccountsUsersService.createAccount(payload);
                const newAccount = {
                    id_pub: res.id_pub,
                    pub_name: payload.pub_name,
                    prefix: res.prefix,
                    hired_services: { dist: '0', conv: '0', ingram: '0', biblio: '0' },
                    account_number_ingram: '',
                    account_login_id_ingram: '',
                };
                setAccountModal({ open: false, isNew: true, account: null });
                // Open the services window so the services can be configured right away.
                setServicesModal({ open: true, account: newAccount });
                setRefreshKey((k) => k + 1);
                return { ok: true, openServices: true };
            } else {
                const idPub = accountModal.account.id_pub;
                await AccountsUsersService.updateAccount({
                    id_pub: idPub,
                    org_name: payload.org_name,
                    type: payload.type,
                    account_tier: payload.account_tier,
                    contract_end: payload.contract_end,
                    metabooks_id: payload.metabooks_id,
                });
                flash('Cuenta actualizada correctamente');
                setAccountModal({ open: false, isNew: true, account: null });
                setRefreshKey((k) => k + 1);
                return { ok: true };
            }
        } catch (e) {
            return { ok: false, msg: e.message };
        } finally {
            setSaving(false);
        }
    };

    // Services (standalone)
    const openServices = (acc) => setServicesModal({ open: true, account: acc });
    const openBilling = (acc) => setBillingModal({ open: true, account: acc });
    const openBank = (acc) => setBankModal({ open: true, account: acc });
    const openPercentagesFromServices = (acc) => setPercentModal({ open: true, account: acc, initialTab: 'add' });
    const openPercentagesSearch = () => setPercentModal({ open: true, account: null, initialTab: 'search' });
    const handleSaveServices = async (payload) => {
        setSaving(true);
        try {
            await AccountsUsersService.updateServices(payload);
            setServicesModal({ open: false, account: null });
            flash('Servicios actualizados correctamente');
            setRefreshKey((k) => k + 1);
            return { ok: true };
        } catch (e) {
            return { ok: false, msg: e.message };
        } finally {
            setSaving(false);
        }
    };

    // Deactivate
    const handleConfirmDeactivate = async () => {
        setSaving(true);
        try {
            await AccountsUsersService.deactivateAccount(deactivateTarget.id_pub);
            setDeactivateTarget(null);
            flash('Cuenta desactivada correctamente');
            setRefreshKey((k) => k + 1);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    // Delete (cascade users)
    const handleConfirmDelete = async () => {
        setSaving(true);
        try {
            await AccountsUsersService.deleteAccount(deleteTarget.id_pub);
            setDeleteTarget(null);
            flash('Cuenta y sus usuarios eliminados');
            setRefreshKey((k) => k + 1);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    // Export (respects applied filters)
    const handleExport = async () => {
        try {
            const blob = await AccountsUsersService.exportAccounts(filters);
            downloadBlob(blob, `cuentas_${new Date().toISOString().slice(0, 10)}.csv`);
        } catch (e) {
            setError(e.message);
        }
    };

    return (
        <>
            {error && (
                <div className="alert alert-danger py-2">{error}
                    <button className="btn-close ms-2 float-end" onClick={() => setError('')} />
                </div>
            )}
            {feedback && (
                <div className="alert alert-success py-2">{feedback}
                    <button className="btn-close ms-2 float-end" onClick={() => setFeedback('')} />
                </div>
            )}

            <AccountsFilters
                filters={{ ...filters, prefix: drafts.prefix, search: drafts.search }}
                onChange={handleFilterChange}
                onSearch={handleSearch}
                onReset={handleReset}
            />

            <div className="d-flex justify-content-end mt-3">
                <button className="btn btn-outline-secondary border-secondary text-white-50 px-3" onClick={openPercentagesSearch} title="Buscar porcentajes Ink It / Editoriales">
                    <FontAwesomeIcon icon={faPercent} className="me-1" /> Buscar porcentajes
                </button>
            </div>

            <div className="row mb-3 mt-4">
                <div className="col-md-12 d-flex justify-content-between align-items-center">
                    <span className="fw-bold small text-uppercase text-white opacity-75">{total} cuenta(s)</span>
                    <div className="d-flex gap-2">
                        <button className="btn btn-outline-secondary border-secondary text-white-50 px-3" onClick={handleExport} title="Exportar CSV">
                            <FontAwesomeIcon icon={faFileCsv} /> Exportar
                        </button>
                        <button className="btn btn-ink-icon" onClick={() => setRefreshKey(k => k + 1)} title="Recargar lista">
                            <FontAwesomeIcon icon={faSync} spin={loading} />
                        </button>
                        <button className="btn btn-success px-3" onClick={openCreate} title="Agregar cuenta">
                            <FontAwesomeIcon icon={faPlus} className="me-1" /> Agregar
                        </button>
                    </div>
                </div>
            </div>

            {loading ? <Loading /> : (
                <>
                    <AccountsTable
                        accounts={items}
                        onEdit={openEdit}
                        onServices={openServices}
                        onBilling={openBilling}
                        onBank={openBank}
                        onOpenPercentages={openPercentagesFromServices}
                        onDeactivate={(acc) => setDeactivateTarget(acc)}
                        onDelete={(acc) => setDeleteTarget(acc)}
                    />
                    {total > perPage && (
                        <Paginator totalItems={total} itemsPerPage={perPage} currentPage={page} onPageChange={setPage} />
                    )}
                </>
            )}

            <AccountModal
                isOpen={accountModal.open}
                onClose={() => setAccountModal({ open: false, isNew: true, account: null })}
                initial={accountModal.account}
                isNew={accountModal.isNew}
                onSave={handleSaveAccount}
                saving={saving}
            />

            <AccountServicesModal
                isOpen={servicesModal.open}
                onClose={() => setServicesModal({ open: false, account: null })}
                account={servicesModal.account}
                onSave={handleSaveServices}
                saving={saving}
                onOpenPercentages={openPercentagesFromServices}
            />

            <PercentPublishersModal
                isOpen={percentModal.open}
                onClose={() => setPercentModal({ open: false, account: null, initialTab: 'add' })}
                initialTab={percentModal.initialTab}
                account={percentModal.account}
            />

            <BillingDataModal
                isOpen={billingModal.open}
                onClose={() => setBillingModal({ open: false, account: null })}
                account={billingModal.account}
                onSaved={flash}
            />

            <BankInfoModal
                isOpen={bankModal.open}
                onClose={() => setBankModal({ open: false, account: null })}
                account={bankModal.account}
                onSaved={flash}
            />

            <ConfirmationModal
                isOpen={deactivateTarget !== null}
                onClose={() => setDeactivateTarget(null)}
                onConfirm={handleConfirmDeactivate}
                title="Desactivar cuenta"
                message={`¿Estás seguro de desactivar la cuenta "${deactivateTarget ? deactivateTarget.pub_name : ''}"?`}
                confirmText="Desactivar"
                type="warning"
                isLoading={saving}
            />

            <ConfirmationModal
                isOpen={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                title="Eliminar cuenta"
                message={`Se eliminará la cuenta "${deleteTarget ? deleteTarget.pub_name : ''}" y sus usuarios asociados. Esta acción no se puede deshacer.`}
                confirmText="Eliminar"
                type="danger"
                isLoading={saving}
            />
        </>
    );
}

export default AccountsTab;